// src/components/dashboard/UnitEdit/UnitRent/UnitRent.tsx
import React from 'react';
import { Formik, Form, Field } from 'formik';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import UICollapsibleSection from '@/components/shared/UICollapsibleSection';
import Input from '@/components/shared/Input';
import Button from '@/components/shared/Button';
import { PaymentMethod, PaymentPeriod } from '@/types';
import './UnitRent.scss';

export interface UnitRentData {
  rentPrice: number | null;
  rentPricePeriod: PaymentPeriod;
  costOfServices: number | null;
  costOfServicesPeriod: PaymentPeriod;
  totalRentPrice: number | null;
  totalRentPricePeriod: PaymentPeriod;
  rentPriceDescription: string;
  firstPaymentDate: Date | null;
  paymentFirstDay: PaymentPeriod;
  paymentMethod: PaymentMethod;
  paymentMethodOtherSpecify: string;
}

export interface UnitRentProps {
  /**
   * Дані секції
   */
  data: UnitRentData;

  /**
   * Callback для збереження даних
   */
  onSave: (data: Partial<UnitRentData>) => Promise<void>;

  /**
   * Стан завантаження
   */
  loading?: boolean;

  /**
   * Контрольований стан згорнутості
   */
  collapsed?: boolean;

  /**
   * Обробник зміни стану згорнутості
   */
  onToggleCollapse?: (collapsed: boolean) => void;

  /**
   * Додатковий CSS клас
   */
  className?: string;
}

const baseClass = 'UnitRent';

/**
 * Компонент для відображення та редагування інформації про оплату оренди
 */
const UnitRent: React.FC<UnitRentProps> = ({
                                             data,
                                             onSave,
                                             loading = false,
                                             collapsed,
                                             onToggleCollapse,
                                             className = '',
                                           }) => {
  const handleSubmit = async (values: UnitRentData) => {
    await onSave(values);
  };

  const handleClear = (resetForm: () => void) => {
    resetForm();
  };

  return (
    <UICollapsibleSection
      title="Rent"
      description=""
      collapsible={true}
      collapsed={collapsed}
      onToggleCollapse={onToggleCollapse}
      loading={loading}
      loadingText="Loading rent information..."
      className={`${baseClass} ${className}`.trim()}
    >
      <Formik
        enableReinitialize={true}
        initialValues={data}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue, errors, touched, handleChange, handleBlur, resetForm }) => (
          <Form className={`${baseClass}__form`}>
            {/* Rent prices grid */}
            <div className={`${baseClass}__prices-grid`}>
              {/* Rent price */}
              <div className={`${baseClass}__field-label`}>Rent</div>
              <Input
                className={`${baseClass}__field-input`}
                onChange={handleChange}
                type="number"
                onBlur={handleBlur}
                placeholder="Rent"
                value={values.rentPrice ?? ''}
                name="rentPrice"
                errors={errors.rentPrice && touched.rentPrice ? errors.rentPrice : ''}
              />
              <div className={`${baseClass}__radio-options`}>
                <label className={`${baseClass}__radio-label`}>
                  <Field
                    className={`${baseClass}__radio-input`}
                    type="radio"
                    name="rentPricePeriod"
                    value={PaymentPeriod.MONTH}
                    onChange={() => setFieldValue('rentPricePeriod', PaymentPeriod.MONTH)}
                  />
                  per month
                </label>
                <label className={`${baseClass}__radio-label`}>
                  <Field
                    className={`${baseClass}__radio-input`}
                    type="radio"
                    name="rentPricePeriod"
                    value={PaymentPeriod.WEEK}
                    onChange={() => setFieldValue('rentPricePeriod', PaymentPeriod.WEEK)}
                  />
                  per week
                </label>
              </div>

              {/* Cost of services */}
              <div className={`${baseClass}__field-label`}>Cost of services</div>
              <Input
                className={`${baseClass}__field-input`}
                onChange={handleChange}
                type="number"
                onBlur={handleBlur}
                placeholder="Cost of services"
                value={values.costOfServices ?? ''}
                name="costOfServices"
                errors={
                  errors.costOfServices && touched.costOfServices
                    ? errors.costOfServices
                    : ''
                }
              />
              <div className={`${baseClass}__radio-options`}>
                <label className={`${baseClass}__radio-label`}>
                  <Field
                    className={`${baseClass}__radio-input`}
                    type="radio"
                    name="costOfServicesPeriod"
                    value={PaymentPeriod.MONTH}
                    onChange={() => setFieldValue('costOfServicesPeriod', PaymentPeriod.MONTH)}
                  />
                  per month
                </label>
                <label className={`${baseClass}__radio-label`}>
                  <Field
                    className={`${baseClass}__radio-input`}
                    type="radio"
                    name="costOfServicesPeriod"
                    value={PaymentPeriod.WEEK}
                    onChange={() => setFieldValue('costOfServicesPeriod', PaymentPeriod.WEEK)}
                  />
                  per week
                </label>
              </div>

              {/* Total rent price */}
              <div className={`${baseClass}__field-label`}>Total</div>
              <Input
                className={`${baseClass}__field-input`}
                onChange={handleChange}
                type="number"
                onBlur={handleBlur}
                placeholder="Total"
                value={values.totalRentPrice ?? ''}
                name="totalRentPrice"
                errors={
                  errors.totalRentPrice && touched.totalRentPrice
                    ? errors.totalRentPrice
                    : ''
                }
              />
              <div className={`${baseClass}__radio-options`}>
                <label className={`${baseClass}__radio-label`}>
                  <Field
                    className={`${baseClass}__radio-input`}
                    type="radio"
                    name="totalRentPricePeriod"
                    value={PaymentPeriod.MONTH}
                    onChange={() => setFieldValue('totalRentPricePeriod', PaymentPeriod.MONTH)}
                  />
                  per month
                </label>
                <label className={`${baseClass}__radio-label`}>
                  <Field
                    className={`${baseClass}__radio-input`}
                    type="radio"
                    name="totalRentPricePeriod"
                    value={PaymentPeriod.WEEK}
                    onChange={() => setFieldValue('totalRentPricePeriod', PaymentPeriod.WEEK)}
                  />
                  per week
                </label>
              </div>
            </div>

            {/* Rent price description */}
            <div className={`${baseClass}__description`}>
              <Field
                className={`${baseClass}__textarea`}
                as="textarea"
                placeholder="Rent price description"
                name="rentPriceDescription"
                value={values.rentPriceDescription}
                onChange={handleChange}
              />
            </div>

            {/* Payment periods section */}
            <div className={`${baseClass}__section-title`}>Payment periods</div>

            {/* First payment date */}
            <div className={`${baseClass}__payment-row`}>
              <span className={`${baseClass}__payment-label`}>First payment date</span>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  className={`${baseClass}__date-picker`}
                  slotProps={{
                    textField: {
                      placeholder: 'First payment date',
                    },
                  }}
                  value={values.firstPaymentDate ? moment(values.firstPaymentDate) : null}
                  onChange={(newValue) => {
                    setFieldValue('firstPaymentDate', newValue ? newValue.toDate() : null);
                  }}
                />
              </LocalizationProvider>
            </div>

            {/* Other payment periods */}
            <div className={`${baseClass}__section-title`}>Other payment periods</div>
            <div className={`${baseClass}__payment-row`}>
              <span className={`${baseClass}__payment-label`}>
                The rent will be paid on the 1st day
              </span>
              <div className={`${baseClass}__radio-options`}>
                <label className={`${baseClass}__radio-label`}>
                  <Field
                    className={`${baseClass}__radio-input`}
                    type="radio"
                    name="paymentFirstDay"
                    value={PaymentPeriod.MONTH}
                    onChange={() => setFieldValue('paymentFirstDay', PaymentPeriod.MONTH)}
                  />
                  of the month
                </label>
                <label className={`${baseClass}__radio-label`}>
                  <Field
                    className={`${baseClass}__radio-input`}
                    type="radio"
                    name="paymentFirstDay"
                    value={PaymentPeriod.WEEK}
                    onChange={() => setFieldValue('paymentFirstDay', PaymentPeriod.WEEK)}
                  />
                  of the week
                </label>
              </div>
            </div>

            {/* Method of payment */}
            <div className={`${baseClass}__section-title`}>Method of payment</div>
            <div className={`${baseClass}__payment-methods`}>
              <div className={`${baseClass}__payment-row`}>
                <div className={`${baseClass}__radio-options`}>
                  <label className={`${baseClass}__radio-label`}>
                    <Field
                      className={`${baseClass}__radio-input`}
                      type="radio"
                      name="paymentMethod"
                      value={PaymentMethod.CASH}
                      onChange={() => setFieldValue('paymentMethod', PaymentMethod.CASH)}
                    />
                    Cash
                  </label>
                  <label className={`${baseClass}__radio-label`}>
                    <Field
                      className={`${baseClass}__radio-input`}
                      type="radio"
                      name="paymentMethod"
                      value={PaymentMethod.CHEQUE}
                      onChange={() => setFieldValue('paymentMethod', PaymentMethod.CHEQUE)}
                    />
                    Cheque
                  </label>
                  <label className={`${baseClass}__radio-label`}>
                    <Field
                      className={`${baseClass}__radio-input`}
                      type="radio"
                      name="paymentMethod"
                      value={PaymentMethod.ELECTRONIC_TRANSFER}
                      onChange={() =>
                        setFieldValue('paymentMethod', PaymentMethod.ELECTRONIC_TRANSFER)
                      }
                    />
                    Electronic bank transfer
                  </label>
                </div>
              </div>
              <div className={`${baseClass}__payment-row`}>
                <div className={`${baseClass}__radio-options`}>
                  <label className={`${baseClass}__radio-label`}>
                    <Field
                      className={`${baseClass}__radio-input`}
                      type="radio"
                      name="paymentMethod"
                      value={PaymentMethod.OTHER}
                      onChange={() => setFieldValue('paymentMethod', PaymentMethod.OTHER)}
                    />
                    Other
                  </label>
                </div>
                {values.paymentMethod === PaymentMethod.OTHER && (
                  <Input
                    onChange={handleChange}
                    onBlur={handleBlur}
                    name="paymentMethodOtherSpecify"
                    value={values.paymentMethodOtherSpecify}
                    placeholder="Specify"
                    className={`${baseClass}__other-input`}
                    errors={
                      errors.paymentMethodOtherSpecify && touched.paymentMethodOtherSpecify
                        ? errors.paymentMethodOtherSpecify
                        : ''
                    }
                  />
                )}
              </div>
            </div>

            {/* Action buttons */}
            <div className={`${baseClass}__actions`}>
              <Button type="submit" theme="primary">
                Save
              </Button>
              <Button
                type="button"
                theme="secondary"
                onClick={() => handleClear(resetForm)}
              >
                Clear
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </UICollapsibleSection>
  );
};

export default UnitRent;